/*
 * The icon set a category can be given (102).
 *
 * A closed, statically-imported registry rather than the whole of @mui/icons-material. The
 * package ships ~2,100 icons in five themes; importing the barrel to enumerate them pulls all
 * of it into the bundle, and this app has no code splitting, so it would land on the student
 * dashboard as well as the admin screen. Resolving a name at render time with a dynamic import
 * instead makes webpack emit a context over those ten thousand modules at build time.
 *
 * Being closed also matters for correctness: `category.icon` is a name out of the database
 * rendered on a student-facing screen, and a name that cannot resolve is impossible here rather
 * than a blank square in the dashboard header.
 *
 * Rounded throughout -- one theme, so a row of tiles reads as a set. Groups are the picker's
 * headings and its search terms both: the filter matches the group name as well as the icon
 * name, so "legal" finds the gavel and the scales.
 *
 * Adding an icon is one line in one group. Nothing else in the feature enumerates icons: the
 * picker, the AI suggestion call and the tile all read this file.
 */
import {
	// Property & Housing
	HomeRounded,
	HomeWorkRounded,
	MapsHomeWorkRounded,
	ApartmentRounded,
	VillaRounded,
	CottageRounded,
	HolidayVillageRounded,
	NightShelterRounded,
	FoundationRounded,
	DomainRounded,
	LocationCityRounded,
	KeyRounded,
	VpnKeyRounded,
	SquareFootRounded,
	ArchitectureRounded,
	FenceRounded,
	YardRounded,
	GrassRounded,
	// Business & Retail
	BusinessRounded,
	CorporateFareRounded,
	StorefrontRounded,
	WarehouseRounded,
	FactoryRounded,
	BusinessCenterRounded,
	WorkRounded,
	BadgeRounded,
	HandshakeRounded,
	SupportAgentRounded,
	CampaignRounded,
	ShoppingCartRounded,
	InventoryRounded,
	PointOfSaleRounded,
	GroupsRounded,
	PeopleRounded,
	PersonRounded,
	// Trades & Construction
	ConstructionRounded,
	HandymanRounded,
	HomeRepairServiceRounded,
	CarpenterRounded,
	PlumbingRounded,
	ElectricalServicesRounded,
	ElectricBoltRounded,
	BoltRounded,
	HvacRounded,
	AcUnitRounded,
	HeatPumpRounded,
	PropaneTankRounded,
	RoofingRounded,
	BuildRounded,
	BuildCircleRounded,
	PrecisionManufacturingRounded,
	EngineeringRounded,
	CleaningServicesRounded,
	PestControlRounded,
	AgricultureRounded,
	// Legal & Compliance
	GavelRounded,
	BalanceRounded,
	AccountBalanceRounded,
	PolicyRounded,
	DescriptionRounded,
	ArticleRounded,
	AssignmentRounded,
	FactCheckRounded,
	RuleRounded,
	ChecklistRounded,
	TaskAltRounded,
	ContentPasteSearchRounded,
	VerifiedRounded,
	VerifiedUserRounded,
	LocalPoliceRounded,
	SecurityRounded,
	ShieldRounded,
	LockRounded,
	LockPersonRounded,
	AdminPanelSettingsRounded,
	GppGoodRounded,
	// Finance & Insurance
	MonetizationOnRounded,
	AttachMoneyRounded,
	PaymentsRounded,
	CreditCardRounded,
	SavingsRounded,
	AccountBalanceWalletRounded,
	RequestQuoteRounded,
	ReceiptLongRounded,
	CurrencyExchangeRounded,
	LocalAtmRounded,
	CalculateRounded,
	FunctionsRounded,
	TrendingUpRounded,
	BarChartRounded,
	ShowChartRounded,
	PieChartRounded,
	QueryStatsRounded,
	InsightsRounded,
	// Health & Medical
	LocalHospitalRounded,
	MedicalServicesRounded,
	MedicalInformationRounded,
	HealthAndSafetyRounded,
	MonitorHeartRounded,
	FavoriteRounded,
	VaccinesRounded,
	MedicationRounded,
	LocalPharmacyRounded,
	BloodtypeRounded,
	HealingRounded,
	CoronavirusRounded,
	SanitizerRounded,
	MasksRounded,
	AccessibleRounded,
	ElderlyRounded,
	PregnantWomanRounded,
	ChildCareRounded,
	FamilyRestroomRounded,
	VisibilityRounded,
	HearingRounded,
	PsychologyRounded,
	PsychologyAltRounded,
	MonitorWeightRounded,
	PetsRounded,
	// Beauty & Wellness
	SpaRounded,
	ContentCutRounded,
	BrushRounded,
	FaceRetouchingNaturalRounded,
	FaceRounded,
	SelfImprovementRounded,
	FitnessCenterRounded,
	SportsRounded,
	SportsSoccerRounded,
	SportsBasketballRounded,
	DirectionsRunRounded,
	PoolRounded,
	// Food & Hospitality
	RestaurantRounded,
	RestaurantMenuRounded,
	LocalDiningRounded,
	LocalCafeRounded,
	LocalBarRounded,
	LiquorRounded,
	BakeryDiningRounded,
	LunchDiningRounded,
	KitchenRounded,
	SoupKitchenRounded,
	HotelRounded,
	LuggageRounded,
	BeachAccessRounded,
	// Safety & Emergency
	LocalFireDepartmentRounded,
	FireExtinguisherRounded,
	FireTruckRounded,
	WarningRounded,
	ReportProblemRounded,
	RadarRounded,
	TrafficRounded,
	// Transport & Logistics
	DirectionsCarRounded,
	DirectionsBusRounded,
	LocalTaxiRounded,
	TwoWheelerRounded,
	LocalShippingRounded,
	CarRepairRounded,
	TireRepairRounded,
	LocalGasStationRounded,
	EvStationRounded,
	ElectricCarRounded,
	DirectionsBoatRounded,
	SailingRounded,
	AnchorRounded,
	FlightRounded,
	FlightTakeoffRounded,
	AirplanemodeActiveRounded,
	TrainRounded,
	SubwayRounded,
	RocketLaunchRounded,
	SatelliteAltRounded,
	// Technology & Data
	ComputerRounded,
	LaptopRounded,
	PhoneIphoneRounded,
	DevicesRounded,
	MemoryRounded,
	CodeRounded,
	TerminalRounded,
	DataObjectRounded,
	DeveloperModeRounded,
	StorageRounded,
	DnsRounded,
	CloudRounded,
	LanRounded,
	RouterRounded,
	WifiRounded,
	BugReportRounded,
	HubRounded,
	AccountTreeRounded,
	LayersRounded,
	DashboardRounded,
	WidgetsRounded,
	ExtensionRounded,
	// Science & Environment
	ScienceRounded,
	BiotechRounded,
	NatureRounded,
	ForestRounded,
	ParkRounded,
	WaterRounded,
	WaterDropRounded,
	SolarPowerRounded,
	WindPowerRounded,
	RecyclingRounded,
	EnergySavingsLeafRounded,
	OilBarrelRounded,
	GasMeterRounded,
	ThermostatRounded,
	ScaleRounded,
	StraightenRounded,
	SpeedRounded,
	PublicRounded,
	WbSunnyRounded,
	CloudQueueRounded,
	// Education & Training
	SchoolRounded,
	MenuBookRounded,
	AutoStoriesRounded,
	LibraryBooksRounded,
	LocalLibraryRounded,
	HistoryEduRounded,
	CastForEducationRounded,
	QuizRounded,
	GradeRounded,
	StarRounded,
	WorkspacePremiumRounded,
	MilitaryTechRounded,
	EmojiEventsRounded,
	LightbulbRounded,
	AutoAwesomeRounded,
	// Arts & Media
	PaletteRounded,
	DesignServicesRounded,
	DrawRounded,
	EditNoteRounded,
	CreateRounded,
	PhotoCameraRounded,
	MovieRounded,
	MusicNoteRounded,
	HeadphonesRounded,
	MicRounded,
	RecordVoiceOverRounded,
	TheaterComedyRounded,
	// Places & Communication
	ExploreRounded,
	MapRounded,
	PlaceRounded,
	TravelExploreRounded,
	LanguageRounded,
	TranslateRounded,
	EmailRounded,
	PhoneRounded,
	ChatRounded,
	ForumRounded,
	NotificationsRounded,
	ShareRounded,
	CalendarMonthRounded,
	ScheduleRounded,
	TimerRounded,
	AlarmRounded,
	NightlightRounded,
	CategoryRounded,
	TokenRounded,
	SettingsRounded,
} from '@mui/icons-material'

export const CATEGORY_ICON_GROUPS = [
	{
		group: 'Property & Housing',
		icons: {HomeRounded, HomeWorkRounded, MapsHomeWorkRounded, ApartmentRounded, VillaRounded, CottageRounded, HolidayVillageRounded, NightShelterRounded, FoundationRounded, DomainRounded, LocationCityRounded, KeyRounded, VpnKeyRounded, SquareFootRounded, ArchitectureRounded, FenceRounded, YardRounded, GrassRounded},
	},
	{
		group: 'Business & Retail',
		icons: {BusinessRounded, CorporateFareRounded, StorefrontRounded, WarehouseRounded, FactoryRounded, BusinessCenterRounded, WorkRounded, BadgeRounded, HandshakeRounded, SupportAgentRounded, CampaignRounded, ShoppingCartRounded, InventoryRounded, PointOfSaleRounded, GroupsRounded, PeopleRounded, PersonRounded},
	},
	{
		group: 'Trades & Construction',
		icons: {ConstructionRounded, HandymanRounded, HomeRepairServiceRounded, CarpenterRounded, PlumbingRounded, ElectricalServicesRounded, ElectricBoltRounded, BoltRounded, HvacRounded, AcUnitRounded, HeatPumpRounded, PropaneTankRounded, RoofingRounded, BuildRounded, BuildCircleRounded, PrecisionManufacturingRounded, EngineeringRounded, CleaningServicesRounded, PestControlRounded, AgricultureRounded},
	},
	{
		group: 'Legal & Compliance',
		icons: {GavelRounded, BalanceRounded, AccountBalanceRounded, PolicyRounded, DescriptionRounded, ArticleRounded, AssignmentRounded, FactCheckRounded, RuleRounded, ChecklistRounded, TaskAltRounded, ContentPasteSearchRounded, VerifiedRounded, VerifiedUserRounded, LocalPoliceRounded, SecurityRounded, ShieldRounded, LockRounded, LockPersonRounded, AdminPanelSettingsRounded, GppGoodRounded},
	},
	{
		group: 'Finance & Insurance',
		icons: {MonetizationOnRounded, AttachMoneyRounded, PaymentsRounded, CreditCardRounded, SavingsRounded, AccountBalanceWalletRounded, RequestQuoteRounded, ReceiptLongRounded, CurrencyExchangeRounded, LocalAtmRounded, CalculateRounded, FunctionsRounded, TrendingUpRounded, BarChartRounded, ShowChartRounded, PieChartRounded, QueryStatsRounded, InsightsRounded},
	},
	{
		group: 'Health & Medical',
		icons: {LocalHospitalRounded, MedicalServicesRounded, MedicalInformationRounded, HealthAndSafetyRounded, MonitorHeartRounded, FavoriteRounded, VaccinesRounded, MedicationRounded, LocalPharmacyRounded, BloodtypeRounded, HealingRounded, CoronavirusRounded, SanitizerRounded, MasksRounded, AccessibleRounded, ElderlyRounded, PregnantWomanRounded, ChildCareRounded, FamilyRestroomRounded, VisibilityRounded, HearingRounded, PsychologyRounded, PsychologyAltRounded, MonitorWeightRounded, PetsRounded},
	},
	{
		group: 'Beauty & Wellness',
		icons: {SpaRounded, ContentCutRounded, BrushRounded, FaceRetouchingNaturalRounded, FaceRounded, SelfImprovementRounded, FitnessCenterRounded, SportsRounded, SportsSoccerRounded, SportsBasketballRounded, DirectionsRunRounded, PoolRounded},
	},
	{
		group: 'Food & Hospitality',
		icons: {RestaurantRounded, RestaurantMenuRounded, LocalDiningRounded, LocalCafeRounded, LocalBarRounded, LiquorRounded, BakeryDiningRounded, LunchDiningRounded, KitchenRounded, SoupKitchenRounded, HotelRounded, LuggageRounded, BeachAccessRounded},
	},
	{
		group: 'Safety & Emergency',
		icons: {LocalFireDepartmentRounded, FireExtinguisherRounded, FireTruckRounded, WarningRounded, ReportProblemRounded, RadarRounded, TrafficRounded},
	},
	{
		group: 'Transport & Logistics',
		icons: {DirectionsCarRounded, DirectionsBusRounded, LocalTaxiRounded, TwoWheelerRounded, LocalShippingRounded, CarRepairRounded, TireRepairRounded, LocalGasStationRounded, EvStationRounded, ElectricCarRounded, DirectionsBoatRounded, SailingRounded, AnchorRounded, FlightRounded, FlightTakeoffRounded, AirplanemodeActiveRounded, TrainRounded, SubwayRounded, RocketLaunchRounded, SatelliteAltRounded},
	},
	{
		group: 'Technology & Data',
		icons: {ComputerRounded, LaptopRounded, PhoneIphoneRounded, DevicesRounded, MemoryRounded, CodeRounded, TerminalRounded, DataObjectRounded, DeveloperModeRounded, StorageRounded, DnsRounded, CloudRounded, LanRounded, RouterRounded, WifiRounded, BugReportRounded, HubRounded, AccountTreeRounded, LayersRounded, DashboardRounded, WidgetsRounded, ExtensionRounded},
	},
	{
		group: 'Science & Environment',
		icons: {ScienceRounded, BiotechRounded, NatureRounded, ForestRounded, ParkRounded, WaterRounded, WaterDropRounded, SolarPowerRounded, WindPowerRounded, RecyclingRounded, EnergySavingsLeafRounded, OilBarrelRounded, GasMeterRounded, ThermostatRounded, ScaleRounded, StraightenRounded, SpeedRounded, PublicRounded, WbSunnyRounded, CloudQueueRounded},
	},
	{
		group: 'Education & Training',
		icons: {SchoolRounded, MenuBookRounded, AutoStoriesRounded, LibraryBooksRounded, LocalLibraryRounded, HistoryEduRounded, CastForEducationRounded, QuizRounded, GradeRounded, StarRounded, WorkspacePremiumRounded, MilitaryTechRounded, EmojiEventsRounded, LightbulbRounded, AutoAwesomeRounded},
	},
	{
		group: 'Arts & Media',
		icons: {PaletteRounded, DesignServicesRounded, DrawRounded, EditNoteRounded, CreateRounded, PhotoCameraRounded, MovieRounded, MusicNoteRounded, HeadphonesRounded, MicRounded, RecordVoiceOverRounded, TheaterComedyRounded},
	},
	{
		group: 'Places & Communication',
		icons: {ExploreRounded, MapRounded, PlaceRounded, TravelExploreRounded, LanguageRounded, TranslateRounded, EmailRounded, PhoneRounded, ChatRounded, ForumRounded, NotificationsRounded, ShareRounded, CalendarMonthRounded, ScheduleRounded, TimerRounded, AlarmRounded, NightlightRounded, CategoryRounded, TokenRounded, SettingsRounded},
	},
]

// name -> component, flattened once. The picker walks the groups; everything else looks up by name.
export const CATEGORY_ICONS = CATEGORY_ICON_GROUPS.reduce(
	(acc, g) => Object.assign(acc, g.icons),
	{}
)

export const CATEGORY_ICON_NAMES = Object.keys(CATEGORY_ICONS)

/*
 * The fallback. A category with no icon yet, or one naming an icon that has since left the
 * registry, renders this rather than nothing -- every kit on the platform is something someone
 * is studying for.
 */
export const DEFAULT_CATEGORY_ICON = 'SchoolRounded'

export const isValidCategoryIcon = (name) => Boolean(name && CATEGORY_ICONS[name])

export const getCategoryIconComponent = (name) =>
	CATEGORY_ICONS[name] || CATEGORY_ICONS[DEFAULT_CATEGORY_ICON]

// "LocalFireDepartmentRounded" -> "Local Fire Department". What the picker labels and filters on.
export const humanizeIconName = (name) =>
	String(name || '')
		.replace(/Rounded$/, '')
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.trim()
